import { useState } from "react";
import { Briefcase, Edit2, Trash2, Plus, Loader2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateExperiences,
  useDeleteExperience,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { ExperienceFormModal } from "@/pages/profile/employee/sections/components/ExperienceFormModal";
import type { ExperienceDetail, ProfileDetail } from "@/interface/profile";

export function ExperienceSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState<ExperienceDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const {
    data: candidateExpRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateExperiences(profileId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteExperience();

  const experienceList: ExperienceDetail[] =
    candidateExpRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedExp(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (exp: ExperienceDetail) => {
    setSelectedExp(exp);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Work Experience</h2>
          <p className="text-xs text-slate-500">Candidate professional experience records</p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Experience
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="space-y-4">
            {experienceList.map((exp: ExperienceDetail) => {
              const duration =
                exp.start_date || exp.end_date || exp.currently_working
                  ? `${exp.start_date || ""} - ${
                      exp.currently_working ? "Present" : exp.end_date || "Present"
                    }`
                  : exp.startDate || exp.endDate || "";

              const skills = (exp.skills_used || exp.technologies || []).map(
                (skill) =>
                  typeof skill === "object" && skill !== null
                    ? "skill_name" in skill
                      ? skill.skill_name
                      : String(skill)
                    : String(skill),
              );

              return (
                <div
                  key={exp.id}
                  className="flex justify-between items-start p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors gap-4"
                >
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {exp.role}
                      </h4>
                      <p className="text-xs font-medium text-purple-600 dark:text-purple-400">
                        {exp.company}
                        {exp.location && (
                          <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">
                            • {exp.location}
                          </span>
                        )}
                      </p>

                      {duration && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 pt-0.5">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{duration}</span>
                        </div>
                      )}

                      {exp.description && (
                        <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed pt-1">
                          {exp.description}
                        </p>
                      )}

                      {skills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1.5">
                          {skills.map((skillName, idx) => (
                            <span
                              key={`${skillName}-${idx}`}
                              className="px-2 py-0.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-[11px] font-medium rounded-md"
                            >
                              {skillName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleOpenEdit(exp)}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4 text-slate-400" />
                    </button>
                    <button
                      onClick={() => setDeleteTargetId(String(exp.id))}
                      className="p-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-rose-400" />
                    </button>
                  </div>
                </div>
              );
            })}

            {experienceList.length === 0 && (
              <div className="text-center p-8 text-slate-500 text-sm">
                No work experience added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more experience records...
              </span>
            </div>
          )}
        </div>
      )}

      <ExperienceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedExp}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Work Experience"
        description="Are you sure you want to remove this experience record?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}

export default ExperienceSection;
